import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import axios from "axios";
import { toast } from "react-toastify";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Container from "@mui/material/Container";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import CircularProgress from "@mui/material/CircularProgress";
import Logo from "../shared/Logo";

interface ResetPasswordFormData {
  password: string;
  confirmPassword: string;
}

const passwordRegex =
  /^(?=.*[A-Za-z])(?=.*\d)(?=.*[@$!%*#?&])[A-Za-z\d@$!%*#?&]{8,}$/;

const validationSchema = yup.object().shape({
  password: yup
    .string()
    .required("Password richiesta")
    .min(8, "La password deve contenere almeno 8 caratteri")
    .matches(
      passwordRegex,
      "La password deve contenere almeno 8 caratteri con un mix di lettere, numeri e simboli"
    ),
  confirmPassword: yup
    .string()
    .required("Conferma password richiesta")
    .oneOf([yup.ref("password")], "Le password non corrispondono"),
});

export const ResetPassword = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get("token");

  const {
    register,
    handleSubmit,
    formState: { errors, touchedFields },
  } = useForm<ResetPasswordFormData>({
    resolver: yupResolver(validationSchema),
  });

  const onSubmit = async (values: ResetPasswordFormData) => {
    if (!token) {
      toast.error("Token di reset non valido");
      return;
    }

    setIsLoading(true);
    try {
      await axios.post(`/auth/password-change`, {
        restorePasswordToken: token,
        password: values.password,
      });

      toast.success("Password modificata con successo");
      navigate("/login");
    } catch (error) {
      toast.error(
        "Errore durante il reset della password. Il link potrebbe essere scaduto."
      );
    } finally {
      setIsLoading(false);
    }
  };

  if (!token) {
    return (
      <Container component="main" maxWidth="xs">
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            minHeight: "100vh",
          }}
        >
          <Typography variant="h6" color="error">
            Link di reset password non valido o scaduto
          </Typography>
        </Box>
      </Container>
    );
  }

  return (
    <Container component="main" maxWidth="xs">
      <Box
        sx={{
          backgroundColor: "background.paper",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "100vh",
          padding: "3",
        }}
      >
        <Box
          sx={{
            maxWidth: 550,
            px: 3,
            py: "20px",
            width: "100%",
          }}
        >
          <Logo />
          <Typography variant="h5" align="center" sx={{ mb: 3 }}>
            Imposta nuova password
          </Typography>

          <form noValidate onSubmit={handleSubmit(onSubmit)}>
            <Stack spacing={3}>
              <TextField
                type="password"
                fullWidth
                label="Nuova Password"
                error={!!(touchedFields.password && errors.password)}
                helperText={touchedFields.password && errors.password?.message}
                disabled={isLoading}
                {...register("password")}
              />
              <TextField
                type="password"
                fullWidth
                label="Conferma Password"
                error={
                  !!(touchedFields.confirmPassword && errors.confirmPassword)
                }
                helperText={
                  touchedFields.confirmPassword &&
                  errors.confirmPassword?.message
                }
                disabled={isLoading}
                {...register("confirmPassword")}
              />
            </Stack>

            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ mt: 2, display: "block" }}
            >
              La password deve contenere almeno 8 caratteri con un mix di
              lettere, numeri e simboli.
            </Typography>

            <Button
              fullWidth
              size="large"
              sx={{ mt: 3 }}
              type="submit"
              variant="contained"
              disabled={isLoading}
            >
              {isLoading ? (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <CircularProgress size={20} color="inherit" />
                  <span>SALVATAGGIO IN CORSO...</span>
                </Box>
              ) : (
                "SALVA NUOVA PASSWORD"
              )}
            </Button>
          </form>
        </Box>
      </Box>
    </Container>
  );
};

export default ResetPassword;
